/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Scale, Gavel, Server } from 'lucide-react';

const TermsConditions = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <main className="max-w-4xl mx-auto px-4 py-16 md:py-24" data-testid="terms-conditions-page">
      <div className="mb-10 border-b pb-6">
        <div className="inline-flex items-center gap-2 mb-3 text-[#1565C0] text-xs font-bold uppercase tracking-widest">
          <Scale className="w-4 h-4" />
          Legal · Terms
        </div>
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#0A1628] mb-3">Terms & Conditions</h1>
        <p className="text-sm text-gray-500">
          Effective date: 1 January 2026 · EWRF Malaysia (Reg. PPM-001-14-02011979)
        </p>
      </div>

      <article className="prose prose-slate max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">1. Acceptance of Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            By accessing or using this website ("Site") or engaging with the Educational, Welfare and
            Research Foundation Malaysia ("EWRF", "we", "us"), you agree to be bound by these Terms &
            Conditions and our <Link to="/privacy" className="text-[#1565C0] hover:underline">Privacy Policy</Link>.
            If you do not agree, please do not use the Site. These Terms are governed by the laws of
            Malaysia and comply with the <strong>Contracts Act 1950</strong>, the
            <strong> Consumer Protection Act 1999</strong>, the <strong>Electronic Commerce Act 2006</strong>,
            and the <strong>Digital Signature Act 1997</strong>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628] flex items-center gap-2"><Server className="w-5 h-5 text-[#D4AF37]" />2. Acceptable Use & Cybersecurity</h2>
          <p className="text-gray-700">You agree NOT to:</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Attempt to gain unauthorised access to any part of the Site, its servers or connected
              databases (in violation of Malaysia's <strong>Computer Crimes Act 1997</strong> §3 & §4).</li>
            <li>Introduce viruses, trojans, worms, ransomware or other malicious code.</li>
            <li>Conduct denial-of-service (DoS/DDoS) attacks or scraping in excess of good-faith limits.</li>
            <li>Use the Site to distribute unlawful, defamatory, obscene, or harassing content (in violation
              of the <strong>Communications and Multimedia Act 1998</strong> §233).</li>
            <li>Impersonate another individual or entity.</li>
            <li>Circumvent any technical, legal or contractual restriction on the Site.</li>
          </ul>
          <p className="text-gray-700 mt-2">
            Breach may result in immediate termination of access, referral to the Royal Malaysia Police
            Cybercrime Investigation Division and pursuit of civil remedies.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">3. Intellectual Property</h2>
          <p className="text-gray-700">
            All content on this Site — text, images, videos, logos, programme names ("EWRF", "Language
            for Juniors", "CSI+", "STI", "C3G", "VIP"), designs, and source code — is the intellectual
            property of EWRF Malaysia and is protected by the <strong>Copyright Act 1987</strong>, the
            <strong> Trade Marks Act 2019</strong>, and international copyright treaties (Berne Convention,
            WIPO Copyright Treaty). You may share links to the Site freely, but any reproduction,
            adaptation or commercial use requires our prior written consent.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">4. Donations</h2>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>All donations are voluntary and are used exclusively for EWRF's charitable objectives.</li>
            <li>EWRF is registered under the Registrar of Societies Malaysia (ROS) and tax-exempt under
              LHDN <strong>Section 44(6)</strong> of the Income Tax Act 1967. Donation receipts are
              issued for eligible contributions.</li>
            <li>Donations are non-refundable except in the case of a verified processing error.
              Refund requests must be made in writing to <a className="text-[#1565C0] hover:underline" href="mailto:admin@ewrf.org.my">admin@ewrf.org.my</a> within 30 days of the transaction.</li>
            <li>Recurring monthly subscriptions may be cancelled at any time by contacting us.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">5. Volunteers & Programme Participants</h2>
          <p className="text-gray-700">
            Registration as a volunteer or programme participant is subject to acceptance by EWRF and
            compliance with our safeguarding, code of conduct and confidentiality policies. Volunteering
            with EWRF does not create an employment relationship. Programme participation is at the
            individual's own risk; EWRF's liability is limited as set out in §7 below.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">6. Third-Party Links</h2>
          <p className="text-gray-700">
            The Site may contain links to external websites (WhatsApp, payment processors, partner NGOs).
            EWRF has no control over the content, privacy practices or availability of those sites and
            accepts no responsibility for them. Access is at your own risk.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628] flex items-center gap-2"><Gavel className="w-5 h-5 text-[#D4AF37]" />7. Limitation of Liability</h2>
          <p className="text-gray-700">
            The Site and its content are provided "as is". To the maximum extent permitted by Malaysian
            law, EWRF's total liability for any claim arising from or in connection with your use of the
            Site or engagement with our programmes shall not exceed the amount you donated in the twelve
            (12) months preceding the claim, and in no event shall we be liable for indirect, incidental,
            special or consequential damages.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">8. Governing Law & Jurisdiction</h2>
          <p className="text-gray-700">
            These Terms are governed by the laws of Malaysia. Any dispute arising out of or in connection
            with these Terms shall be subject to the exclusive jurisdiction of the courts of Kuala Lumpur.
            For international users, we honour applicable local consumer protection rights where they
            provide greater protection.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">9. Changes to Terms</h2>
          <p className="text-gray-700">
            We may amend these Terms from time to time. The most recent version will always be posted on
            this page with an updated effective date. Your continued use of the Site after changes
            constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section className="border-t pt-6 mt-10">
          <p className="text-xs text-gray-500">
            Questions? Contact us at
            <a href="mailto:admin@ewrf.org.my" className="text-[#1565C0] hover:underline"> admin@ewrf.org.my</a>.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            See also: <Link to="/privacy" className="text-[#1565C0] hover:underline">Privacy Policy</Link>
          </p>
        </section>
      </article>
    </main>
    <Footer />
  </div>
);

export default TermsConditions;
